import { CalendarListing, ProductTypes } from '@/types';

import {
  CateringIcon,
  RepeatIcon,
  Tooltip,
  Typography,
  useTheme,
} from '@silverstein-properties/inspirelabs-ui';
import { CustomersPopover } from '../CustomersPopover';
import { getCalendarListingBackgroundColor } from '@/utils/schedulingUtils';
import { useRef } from 'react';
import { Box } from '@mui/material';

export const EventItem = ({
  event,
  onMoreEventsClick,
}: {
  event: CalendarListing;
  onMoreEventsClick?: (anchorEl: HTMLElement) => void;
}) => {
  const eventRef = useRef<HTMLElement>(null);
  const theme = useTheme();

  const eventTime = new Date(event?.start).toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  const guests = event?.nbOfGuests; // number of guests

  const type = event?.productSnapshot?.type;

  const isRecurringEvent = event?.isRecurringEvent;
  const capacity = event?.capacity;

  const { bgColor, borderColor } = getCalendarListingBackgroundColor(
    event?.status || '',
    event?.bookings || [],
    theme
  );

  return (
    <CustomersPopover
      bookings={event?.bookings || []}
      reccuringEvent={event?.reccuringEvent}
      capacity={capacity}
      timeRange={event?.timeRange}
      title={event?.productSnapshot?.title}
    >
      <Box
        ref={eventRef}
        sx={{
          height: 1,
          px: theme.spacers.xs,
          minHeight: 20,
          borderRadius: `${theme.constants.BORDER_RADIUS.small}px`,
          backgroundColor: bgColor,
          border: `1px solid ${borderColor}`,
          display: 'flex',
          flexDirection: 'column',
          color: theme.palette.primary.main,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography component="span" variant="body3">
            {eventTime}
          </Typography>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: 'fit-content',
            }}
          >
            <Typography variant="body3">{`${guests}/${capacity}`}</Typography>

            {isRecurringEvent && (
              <Tooltip arrow title="This is a recurring event." placement="top">
                <RepeatIcon width={16} color="primary" />
              </Tooltip>
            )}
            {type === ProductTypes.FOOD_SERVICE && (
              <Tooltip
                arrow
                title="This is a time slot for catering orders."
                placement="top"
              >
                <CateringIcon width={16} color="primary" />
              </Tooltip>
            )}
          </Box>
        </Box>
        {event?.eventsGroup &&
        event?.eventsGroup?.length > 0 &&
        onMoreEventsClick ? (
          <Typography
            component="span"
            variant="body3"
            sx={{
              width: 'fit-content',
              borderRadius: `${theme.constants.BORDER_RADIUS.small}px`,
              '&:hover': {
                background: 'rgba(0, 0, 0, 0.1)',
              },
            }}
            onClick={e => {
              e.stopPropagation();
              onMoreEventsClick(eventRef.current as HTMLElement);
            }}
          >
            {`+${event?.eventsGroup?.length} more`}
          </Typography>
        ) : null}
      </Box>
    </CustomersPopover>
  );
};
